import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  KOKORO_ENDPOINT,
  KOKORO_LANGUAGES,
  KOKORO_VOICES,
  checkTtsHealth,
  resetTtsConfigCache,
  synthesizeSpeech,
  voicesForLanguage,
} from '@/lib/kokoroTts';

/**
 * Carte « Synthèse vocale (Kokoro TTS) » du Super Admin → Paramètres → API & IA.
 *
 * Le serveur Kokoro-FastAPI est auto-hébergé (72.61.110.113:8880) et exposé au
 * navigateur uniquement via la fonction backend `kokoro-tts`. Cette carte sert
 * à documenter l'endpoint, vérifier la connexion et tester les voix.
 */
export default function TtsSettings() {
  const [lang, setLang] = useState('fr');
  const [voice, setVoice] = useState('ff_siwis');
  const [speed, setSpeed] = useState(1);
  const [sample, setSample] = useState('Bonjour, bienvenue sur Zifek. Votre assistant peut désormais vous répondre à voix haute.');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [health, setHealth] = useState<{ ok: boolean; message: string; latencyMs?: number } | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [toggling, setToggling] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    supabase
      .from('zifek')
      .select('id, tts_enabled')
      .order('id', { ascending: true })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        const row = data as { id?: number; tts_enabled?: boolean | null } | null;
        if (row) setEnabled(row.tts_enabled !== false);
      })
      .catch(() => {});
  }, []);

  const toggleTts = async (next: boolean) => {
    setToggling(true);
    try {
      const { data } = await supabase
        .from('zifek')
        .select('id')
        .order('id', { ascending: true })
        .limit(1)
        .maybeSingle();
      const id = (data as { id?: number } | null)?.id;
      if (id) {
        await supabase.from('zifek').update({ tts_enabled: next }).eq('id', id);
      }
      setEnabled(next);
      resetTtsConfigCache();
    } finally {
      setToggling(false);
    }
  };

  const voices = useMemo(() => voicesForLanguage(lang), [lang]);

  const onLanguageChange = (code: string) => {
    setLang(code);
    const first = voicesForLanguage(code)[0];
    if (first) setVoice(first.id);
    setAudioUrl(null);
  };

  const testHealth = async () => {
    setTesting(true);
    setHealth(null);
    try {
      const result = await checkTtsHealth();
      setHealth(result);
    } finally {
      setTesting(false);
    }
  };

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      if (audioRef.current) audioRef.current.pause();
      const { url } = await synthesizeSpeech({ text: sample, voice, speed });
      setAudioUrl(url);
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.src = url;
          void audioRef.current.play().catch(() => {});
        }
      }, 60);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Génération impossible.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-background-50 border border-background-200/70 rounded-lg p-5">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
          <i className="ri-volume-up-line text-lg"></i>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="text-sm font-semibold text-foreground-900">Synthèse vocale (Kokoro TTS)</h3>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
              health?.ok ? 'bg-green-100 text-green-700' : 'bg-background-100 text-foreground-500'
            }`}>
              {health ? (health.ok ? 'Opérationnel' : 'Injoignable') : 'Non testé'}
            </span>
          </div>
          <p className="text-xs text-foreground-500 mb-4">
            Transforme n&apos;importe quel texte en voix off MP3. Utilisé par les chats (bouton « Écouter »)
            et par le studio de contenu. Le serveur est auto-hébergé : aucun coût par caractère.
          </p>

          {/* Bascule globale du vocal de sortie */}
          <div className={`flex items-start justify-between gap-3 p-3 rounded-lg mb-4 ${enabled ? 'bg-background-100' : 'bg-amber-50'}`}>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-foreground-800 flex items-center gap-1.5">
                <i className={`${enabled ? 'ri-volume-up-line text-accent-600' : 'ri-volume-mute-line text-amber-600'}`}></i>
                Lecture audio (text-to-speech)
              </p>
              <p className="text-[11px] text-foreground-500 mt-1 leading-relaxed">
                {enabled
                  ? 'Active partout : chats assistant, espace client et studio de contenu.'
                  : 'Désactivée partout. La dictée vocale (micro → texte) et l’IA continuent de fonctionner normalement.'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => toggleTts(!enabled)}
              disabled={toggling}
              aria-pressed={enabled}
              className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 disabled:opacity-50 ${enabled ? 'bg-primary-500' : 'bg-background-300'}`}
              title={enabled ? 'Désactiver la lecture audio' : 'Activer la lecture audio'}
            >
              <span
                className={`absolute top-0.5 w-5 h-5 rounded-full bg-background-50 transition-all ${enabled ? 'left-6' : 'left-0.5'}`}
              ></span>
            </button>
          </div>

          {/* Endpoint */}
          <div className="p-3 rounded-lg bg-background-100 mb-4">
            <p className="text-[11px] font-semibold text-foreground-600 mb-1">Endpoint Kokoro (côté serveur)</p>
            <div className="flex items-center gap-2 flex-wrap">
              <code className="text-[11px] text-foreground-800 font-mono break-all">{KOKORO_ENDPOINT}</code>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-800 font-medium whitespace-nowrap">
                POST · JSON → MP3
              </span>
            </div>
            <p className="text-[10px] text-foreground-400 mt-1.5">
              Le navigateur passe par la fonction backend <span className="font-mono">kokoro-tts</span> (proxy sécurisé, sans CORS).
            </p>
          </div>

          {/* Test de connexion */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <button
              type="button"
              onClick={testHealth}
              disabled={testing}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer border border-primary-300 text-primary-700 bg-primary-50 hover:bg-primary-100 transition-colors disabled:opacity-50"
            >
              {testing ? <><i className="ri-loader-4-line animate-spin"></i>Test en cours…</> : <><i className="ri-plug-line"></i>Tester le serveur vocal</>}
            </button>
            <button
              type="button"
              onClick={generate}
              disabled={busy || !sample.trim()}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer bg-foreground-950 text-background-50 hover:bg-foreground-800 transition-colors disabled:opacity-50"
            >
              {busy ? <><i className="ri-loader-4-line animate-spin"></i>Génération…</> : <><i className="ri-play-circle-line"></i>Écouter un échantillon</>}
            </button>
            {audioUrl && (
              <audio ref={audioRef} src={audioUrl} controls className="h-9 max-w-[220px]" />
            )}
          </div>

          {health && (
            <div className={`mb-3 flex items-start gap-2 px-3 py-2.5 rounded-lg text-xs ${health.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
              <i className={`${health.ok ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} mt-0.5 flex-shrink-0`}></i>
              <span>
                {health.message}
                {typeof health.latencyMs === 'number' && <span className="text-foreground-400"> · {health.latencyMs} ms</span>}
              </span>
            </div>
          )}

          {/* Réglages d'échantillon */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-foreground-600 mb-1">Langue</label>
              <div className="relative">
                <select
                  value={lang}
                  onChange={(e) => onLanguageChange(e.target.value)}
                  className="w-full h-10 px-3 pr-8 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer appearance-none"
                >
                  {KOKORO_LANGUAGES.map((l) => (
                    <option key={l.code} value={l.code}>{l.flag} {l.label}</option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none text-xs"></i>
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-foreground-600 mb-1">Voix ({voices.length})</label>
              <div className="relative">
                <select
                  value={voice}
                  onChange={(e) => { setVoice(e.target.value); setAudioUrl(null); }}
                  className="w-full h-10 px-3 pr-8 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer appearance-none"
                >
                  {voices.map((v) => (
                    <option key={v.id} value={v.id}>{v.label} · {v.gender === 'F' ? 'Féminine' : 'Masculine'}</option>
                  ))}
                </select>
                <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none text-xs"></i>
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-foreground-600 mb-1">Vitesse : {speed.toFixed(2)}×</label>
              <input
                type="range"
                min={0.6}
                max={1.6}
                step={0.05}
                value={speed}
                onChange={(e) => { setSpeed(Number(e.target.value)); setAudioUrl(null); }}
                className="w-full mt-3 accent-[oklch(var(--primary-500))] cursor-pointer"
              />
            </div>
          </div>

          <div className="mt-3">
            <label className="block text-[11px] font-semibold text-foreground-600 mb-1">Texte d&apos;échantillon</label>
            <textarea
              value={sample}
              onChange={(e) => setSample(e.target.value.slice(0, 400))}
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-900 focus:outline-none focus:border-primary-300 resize-none"
            />
            <p className="text-[10px] text-foreground-400 mt-1">
              {sample.length}/400 caractères · {KOKORO_VOICES.length} voix disponibles au total.
            </p>
          </div>

          {error && (
            <p className="text-xs text-red-600 mt-3 flex items-center gap-1.5">
              <i className="ri-error-warning-line"></i>{error}
            </p>
          )}

          <p className="text-[10px] text-foreground-400 mt-3 flex items-start gap-1.5">
            <i className="ri-information-line mt-0.5 flex-shrink-0"></i>
            L&apos;arabe et la darija ne font pas partie du catalogue Kokoro : la lecture retombe automatiquement sur la voix française.
          </p>
        </div>
      </div>
    </section>
  );
}